//MyProfile/MyPostingPresentation.tsx

import React, { useEffect, useState, useRef } from 'react';
import { HLSPlayer } from '../Gallery/GalleryPresentation';

// ---------------- Types ----------------
interface Props {
  post: Post;
  startIndex: number;
  onClose: () => void;
}

export type MediaEntry =
  | { kind: 'image'; url: string }
  | {
      kind: 'video';
      videoId: string;
      url?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    };

export interface Post {
  _id: string;
  type?: string;
  title?: string;
  titleColor?: string;
  media: MediaEntry[];
  likes: number;
  likedBy?: string[];
  captions?: string[];
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  createdAt: string;
  isHidden?: boolean;
  hiddenAt?: string | null;
  user?: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
  isAd?: boolean;
  adLabel?: string;
  adCtaText?: string;
  adUrl?: string;
  advertiserName?: string;
}

    
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');


const videoUrlOf = (m: Extract<MediaEntry, { kind: 'video' }>) => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url;
  return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
};


const mediaUrlOf = (m: MediaEntry): string => {
  if (m.kind === 'video') {
    return videoUrlOf(m);
  }
  return m.url;
};

// ---------------- Component ----------------
const MyPostingPresentation: React.FC<Props> = ({
  post,
  startIndex,
  onClose,
}) => {
 
// Track which videos are landscape
const [isVideoLandscape, setIsVideoLandscape] = useState<Record<number, boolean>>({});
const [currentIndex, setCurrentIndex] = useState(startIndex);

const goPrev = () =>
  setCurrentIndex(i => Math.max(0, i - 1));

const goNext = () =>
  setCurrentIndex(i => Math.min(post.media.length - 1, i + 1));

// Update handlers
const handleImgLoad = (index: number, ev: React.SyntheticEvent<HTMLImageElement>) => {
  const img = ev.currentTarget;
  const w = img.naturalWidth || 0;
  const h = img.naturalHeight || 0;
  const landscape = w >= h;
  setIsLandscape(prev => (prev[index] === landscape ? prev : { ...prev, [index]: landscape }));
};

  // Track which grid images are landscape
const [isLandscape, setIsLandscape] = useState<Record<number, boolean>>({});

useEffect(() => {
  const prev = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  return () => {
    document.body.style.overflow = prev;
  };
}, []);

useEffect(() => {
  setCurrentIndex(startIndex);
}, [post._id, startIndex]);


useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') {
      setCurrentIndex(i => Math.max(0, i - 1));
    }
    if (e.key === 'ArrowRight') {
      setCurrentIndex(i => Math.min(post.media.length - 1, i + 1));
    }
  };

  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [onClose, post.media.length]);


return (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100000,
      backgroundColor: '#000',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
 {/* ❌ CLOSE (Gallery style) */}
<button
  onClick={onClose}
  style={{
    position: "absolute",
    top: 16,
    right: 20,
    zIndex: 100001,
    background: "rgba(0,0,0,0.6)",
    color: "#666",
    border: "none",
    fontSize: 16,
    cursor: "pointer",
    padding: 8,
    borderRadius: 999,
  }}
>
  ✖
</button>

{/* ← PREV (Gallery style) */}
{currentIndex > 0 && (
  <button
    onClick={goPrev}
    style={{
      position: "absolute",
      left: 20,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 28,
      background: "none",
      color: "#666",
      border: "none",
      cursor: "pointer",
    }}
  >
    ‹
  </button>
)}

{/* → NEXT (Gallery style) */}
{currentIndex < post.media.length - 1 && (
  <button
    onClick={goNext}
    style={{
      position: "absolute",
      right: 20,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 28,
      background: "none",
      color: "#666",
      border: "none",
      cursor: "pointer",
    }}
  >
    ›
  </button>
)}


{/* 🎬 ACTIVE MEDIA (Gallery layout) */}
{(() => {
  const m = post.media[currentIndex];
  if (!m) return null;

  const isVid = m.kind === "video";
  const url = mediaUrlOf(m);

  const musicLabel =
    m.kind === "video"
      ? [m.musicTitle, m.musicArtist].filter(Boolean).join(" — ")
      : "";

  const isLandscapeNow =
    (isVid ? isVideoLandscape[currentIndex] : isLandscape[currentIndex]) ?? false;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* MEDIA BLOCK (same as Gallery) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",

          width: isLandscapeNow ? "92vw" : "auto",
          height: isLandscapeNow ? "auto" : "calc(100vh - 28px)",

          maxWidth: isLandscapeNow ? "92vw" : "min(94vw, 760px)",
          maxHeight: isLandscapeNow ? "80vh" : "calc(100vh - 28px)",
        }}
      >
        {isVid ? (
          <HLSPlayer
            src={url}
            muted
            active
            isLandscape={isLandscapeNow}
            onOrientation={(land) => {
              setIsVideoLandscape((prev) =>
                prev[currentIndex] === land ? prev : { ...prev, [currentIndex]: land }
              );
            }}
            style={{
              width: "auto",
              height: "auto",
              maxWidth: isLandscapeNow ? "93vw" : "100vh",
              maxHeight: isLandscapeNow ? "93vh" : "100vh",
              objectFit: "contain",
              borderRadius: 12,
            }}
          />
        ) : (
          <img
            src={url}
            alt="presentation-media"
            onLoad={(e) => handleImgLoad(currentIndex, e)}
            style={{
              width: "auto",
              height: "auto",
              maxWidth: isLandscapeNow ? "93vw" : "97vh",
              maxHeight: isLandscapeNow ? "93vh" : "97vh",
              objectFit: "contain",
              borderRadius: 12,
            }}
          />
        )}
      </div>

      {/* 🎵 MUSIC (Gallery style: fixed bottom, does not shrink media) */}
      {musicLabel && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 12,
            color: "#777",
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0px 5px 3px 5px",
            borderRadius: 999,
            background: "transparent",
            maxWidth: "50vw",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            zIndex: 100002,
          }}
        >
          <span>🎵</span>

          {m.kind === "video" && m.musicId ? (
            <a
              href={`/musicdetails/${m.musicId}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                color: "#aaa",
                textDecoration: "none",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {musicLabel}
            </a>
          ) : (
            <span>{musicLabel}</span>
          )}
        </div>
      )}
    </div>
  );
})()}
</div>
);
};

export default MyPostingPresentation;
